// @ts-nocheck
import * as React from "react";

// PWA 설치/업데이트 UI — 모바일 화면(screen-mobile)에서만 노출한다.
//  · Android/Chrome: beforeinstallprompt 캡처 → "설치" 배너
//  · iOS Safari: beforeinstallprompt 미지원 → "공유 → 홈 화면에 추가" 수동 안내
//  · 업데이트: 서비스워커가 새 버전을 감지하면(design.tsx 가 pwa:update-available 발행)
//    토스트 노출 → "새로고침"이 pwa:apply-update 를 발행(= 대기 SW 활성화 후 reload)
//
// design.tsx 부트스트랩에서 <PwaLayer mobile={…}/> 로 화면과 나란히 마운트한다.

const KEY_INSTALL = "pwa.install.snoozed";
const KEY_IOS = "pwa.ios.snoozed";
const SNOOZE_MS = 14 * 24 * 60 * 60 * 1000; // 14일

function snoozed(key) {
  try {
    const t = Number(localStorage.getItem(key) || 0);
    return t > 0 && Date.now() - t < SNOOZE_MS;
  } catch { return false; }
}
function snooze(key) {
  try { localStorage.setItem(key, String(Date.now())); } catch { /* private mode */ }
}

function isStandalone() {
  try {
    return window.matchMedia?.("(display-mode: standalone)").matches
      || window.navigator.standalone === true; // iOS Safari
  } catch { return false; }
}

function isIosSafari() {
  const ua = navigator.userAgent || "";
  // iPadOS 13+ 는 Macintosh 로 위장 → 터치포인트로 식별
  const iOS = /iphone|ipad|ipod/i.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  const webkit = /WebKit/i.test(ua);
  const otherBrowser = /CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua); // iOS 크롬/파폭 등은 설치 불가
  return iOS && webkit && !otherBrowser;
}

export function PwaLayer({ mobile }) {
  const [deferred, setDeferred] = React.useState(null);
  const [showInstall, setShowInstall] = React.useState(false);
  const [showIos, setShowIos] = React.useState(false);
  const [showUpdate, setShowUpdate] = React.useState(false);

  // 설치 가능 신호(Android) + iOS 수동 안내 판정
  React.useEffect(() => {
    if (!mobile || isStandalone()) return;
    const onBip = (e) => {
      e.preventDefault();          // 브라우저 기본 미니인포바 억제 → 커스텀 배너로
      setDeferred(e);
      if (!snoozed(KEY_INSTALL)) setShowInstall(true);
    };
    const onInstalled = () => { setShowInstall(false); setDeferred(null); };
    window.addEventListener("beforeinstallprompt", onBip);
    window.addEventListener("appinstalled", onInstalled);
    if (isIosSafari() && !snoozed(KEY_IOS)) setShowIos(true);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [mobile]);

  // 업데이트 알림(서비스워커 새 버전)
  React.useEffect(() => {
    const onUpd = () => setShowUpdate(true);
    window.addEventListener("pwa:update-available", onUpd);
    return () => window.removeEventListener("pwa:update-available", onUpd);
  }, []);

  if (!mobile) return null;

  const doInstall = async () => {
    if (!deferred) return;
    setShowInstall(false);
    deferred.prompt();
    try { await deferred.userChoice; } catch { /* 사용자 취소 */ }
    setDeferred(null);
  };
  const dismissInstall = () => { snooze(KEY_INSTALL); setShowInstall(false); };
  const dismissIos = () => { snooze(KEY_IOS); setShowIos(false); };
  const applyUpdate = () => { setShowUpdate(false); window.dispatchEvent(new Event("pwa:apply-update")); };

  // 하단 탭바(~60px) 위로 띄운다. 탭 없는 화면에선 살짝 더 높게 떠도 무방.
  const wrap = {
    position: "absolute", left: 12, right: 12, bottom: 88, zIndex: 200,
    display: "flex", flexDirection: "column", gap: 10, pointerEvents: "none",
    fontFamily: '"Pretendard Variable", Pretendard, system-ui, sans-serif',
  };
  const card = {
    pointerEvents: "auto", background: "var(--surface, #fff)",
    border: "1px solid var(--border-soft, #F1E9D5)", borderRadius: 16,
    boxShadow: "0 12px 32px rgba(42,36,31,.18)", padding: 14,
  };
  const primaryBtn = {
    appearance: "none", border: "none", cursor: "pointer",
    background: "var(--orange-500, #E8825B)", color: "#fff",
    fontWeight: 700, fontSize: 14, padding: "9px 16px", borderRadius: 11,
    fontFamily: "inherit", whiteSpace: "nowrap",
  };
  const ghostBtn = {
    appearance: "none", border: "none", cursor: "pointer", background: "transparent",
    color: "var(--ink-3, #8A8079)", fontWeight: 600, fontSize: 13, padding: "9px 10px",
    fontFamily: "inherit", whiteSpace: "nowrap",
  };

  return (
    <div style={wrap}>
      {showUpdate && (
        <div role="status" style={{ ...card, display: "flex", alignItems: "center", gap: 12,
          background: "var(--ink-1, #2A241F)", border: "none" }}>
          <div style={{ flex: 1, color: "#fff", fontSize: 13.5, fontWeight: 600 }}>
            새 버전이 준비됐어요.
          </div>
          <button onClick={applyUpdate} style={{ ...primaryBtn, padding: "8px 14px" }}>새로고침</button>
        </div>
      )}

      {showInstall && (
        <div role="dialog" aria-label="앱 설치" style={{ ...card, display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 11, flex: "0 0 auto",
            background: "var(--orange-500, #E8825B)", display: "grid", placeItems: "center",
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M12 20s-7-4.5-7-9a4 4 0 0 1 7-2.6A4 4 0 0 1 19 11c0 4.5-7 9-7 9z" fill="#fff" />
            </svg>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "var(--ink-1, #2A241F)" }}>통합돌봄 앱 설치</div>
            <div style={{ fontSize: 12, color: "var(--ink-3, #8A8079)", marginTop: 2 }}>
              홈 화면에 추가하면 더 빠르게 열 수 있어요.
            </div>
          </div>
          <button onClick={doInstall} style={primaryBtn}>설치</button>
          <button onClick={dismissInstall} aria-label="닫기" style={ghostBtn}>✕</button>
        </div>
      )}

      {showIos && (
        <div role="dialog" aria-label="홈 화면에 추가" style={{ ...card }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "var(--ink-1, #2A241F)", flex: 1 }}>
              홈 화면에 추가
            </div>
            <button onClick={dismissIos} style={ghostBtn}>확인</button>
          </div>
          <div style={{ fontSize: 12.5, lineHeight: 1.6, color: "var(--ink-2, #4F4740)" }}>
            하단의 <b>공유</b> 버튼
            <span aria-hidden="true" style={{ margin: "0 4px" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ verticalAlign: "-2px" }}>
                <path d="M12 3v12M12 3l-4 4M12 3l4 4M5 12v7h14v-7" stroke="var(--orange-600, #D26A44)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            을 누른 뒤 <b>‘홈 화면에 추가’</b>를 선택하세요.
          </div>
        </div>
      )}
    </div>
  );
}
